import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, LifeBuoy, Send, Check, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Modale de support intégrée — permet à un utilisateur connecté d'envoyer un
 * message à hello@wyvio.app via Resend. Auto-préremplit user + agence + URL
 * courante + dossier concerné si détectable.
 */
const SupportModal = ({ open, onClose }) => {
  const { user, agency } = useAuth();
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubject("");
      setMessage("");
      setSent(false);
    }
  }, [open]);

  // ESC pour fermer
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const currentUrl = typeof window !== "undefined" ? window.location.href : "";
  const dossierMatch = currentUrl.match(/\/app\/dossier\/([a-f0-9]{24})/i);
  const dossierId = dossierMatch ? dossierMatch[1] : null;

  const canSend = subject.trim().length > 0 && message.trim().length >= 5 && !sending;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setSending(true);
    try {
      await api.sendSupportMessage({
        subject: subject.trim(),
        message: message.trim(),
        current_url: currentUrl,
        dossier_id: dossierId,
      });
      setSent(true);
      toast.success("Message envoyé à l'équipe WYVIO.");
    } catch (err) {
      toast.error(err.message || "Impossible d'envoyer le message.");
    } finally {
      setSending(false);
    }
  };

  const modal = (
    <div
      data-testid="support-modal"
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden
      />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start justify-between p-6 border-b border-[#EEF2F5]">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-2xl bg-[#111] text-[#C8FF2F] flex items-center justify-center">
              <LifeBuoy size={18} strokeWidth={2} />
            </div>
            <div>
              <h2 className="text-[17px] font-semibold text-[#111] tracking-tight">
                Contacter le support
              </h2>
              <p className="mt-0.5 text-[12.5px] text-[#6B7280]">
                Notre équipe répond en moins de 2h ouvrées.
              </p>
            </div>
          </div>
          <button
            type="button"
            data-testid="support-modal-close"
            onClick={onClose}
            className="h-9 w-9 rounded-full hover:bg-[#F7F8FA] flex items-center justify-center text-[#6B7280] hover:text-[#111]"
            aria-label="Fermer"
          >
            <X size={16} strokeWidth={2} />
          </button>
        </div>

        {sent ? (
          <div data-testid="support-modal-success" className="p-8 text-center">
            <div className="mx-auto h-14 w-14 rounded-full bg-[#DCFCE7] flex items-center justify-center">
              <Check size={22} strokeWidth={2.5} className="text-[#166534]" />
            </div>
            <h3 className="mt-5 text-[17px] font-semibold text-[#111]">
              Message envoyé
            </h3>
            <p className="mt-2 text-[13.5px] text-[#6B7280]">
              Nous vous répondons sur <span className="text-[#111] font-medium">{user?.email}</span> dans les meilleurs délais.
            </p>
            <button
              type="button"
              data-testid="support-modal-done"
              onClick={onClose}
              className="mt-6 h-11 rounded-full bg-[#111] text-white text-[13px] font-semibold px-6 hover:bg-black"
            >
              Fermer
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4" data-testid="support-modal-form">
            {/* Contexte pré-rempli */}
            <div className="rounded-2xl bg-[#F7F8FA] px-4 py-3 flex items-start gap-2.5">
              <AlertCircle size={13} strokeWidth={2} className="text-[#6B7280] mt-0.5 shrink-0" />
              <div className="text-[11.5px] text-[#6B7280] leading-relaxed">
                Envoyé en tant que <span className="text-[#111] font-medium">{user?.name || user?.email}</span>
                {agency?.name && <> · <span className="text-[#111] font-medium">{agency.name}</span></>}
                {dossierId && <> · Dossier <span className="text-[#111] font-medium">{dossierId.slice(-6)}</span></>}
              </div>
            </div>

            <div>
              <label className="text-[12px] font-medium text-[#111]">Sujet</label>
              <input
                data-testid="support-modal-subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Ex. Bug lors de la création d'un dossier"
                required
                maxLength={180}
                className="mt-1.5 w-full h-11 rounded-xl bg-white border border-[#EEF2F5] px-3.5 text-[14px] text-[#111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111] transition-colors"
              />
            </div>

            <div>
              <label className="text-[12px] font-medium text-[#111]">Message</label>
              <textarea
                data-testid="support-modal-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Décrivez votre demande avec un maximum de détails (étapes, message d'erreur, ce que vous attendiez…)"
                required
                rows={6}
                minLength={5}
                maxLength={4000}
                className="mt-1.5 w-full rounded-xl bg-white border border-[#EEF2F5] px-3.5 py-3 text-[14px] text-[#111] placeholder:text-[#9CA3AF] outline-none focus:border-[#111] transition-colors resize-none"
              />
              <p className="mt-1 text-[11px] text-[#9CA3AF]">
                {message.length}/4000
              </p>
            </div>

            <button
              type="submit"
              data-testid="support-modal-submit"
              disabled={!canSend}
              className="w-full h-12 rounded-full bg-[#111] text-white text-[13.5px] font-semibold inline-flex items-center justify-center gap-2 hover:bg-black active:scale-[0.99] transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-[0_10px_30px_-10px_rgba(17,17,17,0.4)]"
            >
              {sending ? "Envoi…" : <>Envoyer <Send size={13} strokeWidth={2.25} /></>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
  return createPortal(modal, document.body);
};

export default SupportModal;
